import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { Station } from '../api/types';
import { usePoll } from '../hooks/usePoll';
import { formatBalance, formatMinutes, formatMoney } from '../lib/format';
import { StartSessionDialog } from '../components/StartSessionDialog';
import { StationDetail, TimeUpCard } from '../components/StationDetail';
import { StationList } from '../components/StationList';
import { useToast } from '../components/Toast';
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Modal,
  MONEY_MASK,
  RevealButton,
  Spinner,
} from '../components/ui';

// There is no push channel in the MVP; five seconds is the intended refresh
// rate, and the per-tile countdown keeps the numbers moving in between.
const POLL_MS = 5000;

const MASK = MONEY_MASK;

function SummaryStrip() {
  const { data } = usePoll((signal) => api.dashboard.live(signal), POLL_MS);
  // The floor screen faces the counter, and customers can read it. Takings
  // stay masked until someone taps the eye, and go back to masked on reload.
  const [showTakings, setShowTakings] = useState(false);
  if (!data) return null;

  const totals = data.stations.reduce(
    (acc, row) => ({
      active: acc.active + row.active,
      idle: acc.idle + row.idle,
      offline: acc.offline + row.agents_offline,
    }),
    { active: 0, idle: 0, offline: 0 },
  );

  const stat = (label: string, value: string, tone = 'text-slate-100', sub?: string) => (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-semibold ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-3">
      <Card className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {stat('In use', String(totals.active), 'text-emerald-300')}
        {stat('Free', String(totals.idle))}
        {stat(
          'Agents offline',
          String(totals.offline),
          totals.offline > 0 ? 'text-amber-300' : 'text-slate-100',
        )}
        {/* Money actually taken through the tab. Recharges are collected on
            the tab too, so the value of packs sold is not what was taken. */}
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Today taken</p>
          {/* The eye sits right beside the figure it reveals. A 44px tap
              target, pulled in with negative margins so the row stays the
              same height as its neighbours. */}
          <div className="flex items-center gap-1">
            <p className="text-xl font-semibold text-slate-100">
              {showTakings
                ? formatMoney(
                    data.today.payments_received !== null && data.today.payments_received !== undefined
                      ? data.today.payments_received
                      : data.today.recharge_revenue,
                  )
                : MASK}
            </p>
            <RevealButton
              shown={showTakings}
              onToggle={() => setShowTakings((shown) => !shown)}
              what="today’s takings"
            />
          </div>
          {/* The drawer and the UPI statement are reconciled separately. */}
          {data.today.cash_received !== null && data.today.cash_received !== undefined && (
            <p className="text-xs text-slate-500">
              {showTakings
                ? `${formatMoney(data.today.cash_received)} cash · ${formatMoney(data.today.upi_received ?? 0)} UPI`
                : `${MASK} cash · ${MASK} UPI`}
            </p>
          )}
        </div>
        {/* Both of these read to the second. They sit next to a customer card
            that already says "56m 6s", and a tile that rounds to "56m" reads
            as a different number rather than the same one, less precise. The
            minutes field is the fallback for a backend without the seconds. */}
        {stat(
          'Time played',
          data.today.seconds_played !== null && data.today.seconds_played !== undefined
            ? formatBalance(data.today.seconds_played)
            : formatMinutes(data.today.minutes_played),
        )}
        {/* What the cafe owes in playing time right now, across everyone —
            the number that says how much prepaid business is still to be
            delivered. Split by pool underneath because the pools never mix. */}
        {data.prepaid_outstanding ? (
          stat(
            'Prepaid outstanding',
            formatMinutes(Math.floor(data.prepaid_outstanding.total_seconds / 60)),
            data.prepaid_outstanding.total_seconds > 0 ? 'text-sky-300' : 'text-slate-100',
            `${formatMinutes(Math.floor(data.prepaid_outstanding.general_seconds / 60))} PC · ` +
              `${formatMinutes(Math.floor(data.prepaid_outstanding.simulator_seconds / 60))} sim · ` +
              `${data.prepaid_outstanding.customers} customer${data.prepaid_outstanding.customers === 1 ? '' : 's'}`,
          )
        ) : (
          stat(
            'Expiring in 3 days',
            data.expiring_soon.seconds_expiring !== null &&
              data.expiring_soon.seconds_expiring !== undefined
              ? formatBalance(data.expiring_soon.seconds_expiring)
              : formatMinutes(data.expiring_soon.minutes_expiring),
            data.expiring_soon.minutes_expiring > 0 ? 'text-amber-300' : 'text-slate-100',
          )
        )}
      </Card>

      {/* The five-minute warning is the whole grace period — there is no buffer
          after zero, so this list is the thing staff must not miss. */}
      {data.warnings.length > 0 && (
        <Card className="border-rose-500/50 bg-rose-500/10">
          <p className="text-sm font-semibold text-rose-200">
            Running out now — sell a recharge or tell them to wrap up
          </p>
          <ul className="mt-2 space-y-1 text-sm text-rose-100">
            {data.warnings.map((warning) => (
              <li key={warning.session_id}>
                <span className="font-medium">{warning.station_name}</span> ·{' '}
                {warning.customer_name} · {warning.phone}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function FloorPage() {
  const toast = useToast();
  const { data, error, loading, refresh } = usePoll(
    (signal) => api.stations.list(signal),
    POLL_MS,
  );

  // The floor is a list on the left and one seat's controls on the right. The
  // selection is an id, not a station object, so every 5-second poll redraws
  // the panel with fresh clocks instead of a snapshot from when it was tapped.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // On a phone there is no room for two columns: tapping a row opens the
  // panel in place of the list, and "←" comes back. Desktop shows both.
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (!data || data.length === 0) return;
    // Default to the first seat so the right-hand side is never blank, and
    // fall back to it if the selected station was deleted underneath us.
    if (!selectedId || !data.some((station) => station.station_id === selectedId)) {
      setSelectedId(data[0].station_id);
    }
  }, [data, selectedId]);

  const selected = data?.find((station) => station.station_id === selectedId) ?? null;

  const [seating, setSeating] = useState<Station | null>(null);
  const [stopping, setStopping] = useState<Station | null>(null);
  const [busy, setBusy] = useState(false);

  const stopSession = async (reason: 'staff_stopped' | 'cancelled') => {
    if (!stopping?.active_session) return;
    setBusy(true);
    try {
      const result = await api.sessions.stop(stopping.active_session.session_id, reason);
      toast.success(
        stopping.active_session.source === 'ps5_session'
          ? `${result.customer_name}'s group finished on ${result.station_name}.`
          : `${result.customer_name} finished on ${result.station_name} — ${formatMinutes(result.minutes_deducted)} charged.`,
      );
      setStopping(null);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Floor</h1>
        <Link to="/customers">
          <Button variant="secondary">Customers</Button>
        </Link>
      </div>

      <SummaryStrip />

      {/* PS5s past their booked time, whichever seat is selected. Nothing else
          stops a console, so this sits above the floor until someone acts. */}
      {data?.map((station) =>
        station.time_up && !station.active_session ? (
          <TimeUpCard
            key={station.station_id}
            station={station}
            timeUp={station.time_up}
            onChanged={refresh}
          />
        ) : null,
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner label="Loading the floor…" />
      ) : !data || data.length === 0 ? (
        <EmptyState title="No stations yet.">
          An admin adds the PCs under the Stations tab.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(18rem,22rem)_1fr]">
          <div className={panelOpen ? 'hidden lg:block' : ''}>
            <StationList
              stations={data}
              selectedId={selectedId}
              onSelect={(station) => {
                setSelectedId(station.station_id);
                setPanelOpen(true);
              }}
            />
          </div>
          <div className={panelOpen ? '' : 'hidden lg:block'}>
            {selected ? (
              <StationDetail
                // Keyed so the panel's local state (open tab, dialogs) resets
                // when staff pick a different seat.
                key={selected.station_id}
                station={selected}
                onChanged={refresh}
                onSeat={setSeating}
                onStop={setStopping}
                onBack={() => setPanelOpen(false)}
              />
            ) : (
              <EmptyState title="Pick a station from the list." />
            )}
          </div>
        </div>
      )}

      {seating && (
        <StartSessionDialog
          station={seating}
          onClose={() => setSeating(null)}
          onStarted={refresh}
        />
      )}

      {stopping?.active_session && (
        <Modal title={`Stop ${stopping.name}?`} onClose={() => setStopping(null)}>
          <div className="space-y-4">
            {stopping.active_session.source === 'ps5_session' ? (
              <p className="text-sm text-slate-300">
                Ends the session now. The {formatMoney(stopping.active_session.amount_charged)}{' '}
                already on {stopping.active_session.customer_name}'s tab stays — ending early is
                not refunded. The console does not switch off, so tell the group.
              </p>
            ) : (
              <p className="text-sm text-slate-300">
                {stopping.active_session.customer_name} is charged for the time played
                so far, including the minute in progress, and the PC locks again.
                {stopping.active_session.paused &&
                  ' The session is paused, so nothing extra is charged for the pause.'}
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="danger"
                className="flex-1"
                disabled={busy}
                onClick={() => stopSession('staff_stopped')}
              >
                Stop session
              </Button>
              {/* "Cancelled" exists for a mis-click, so it stays out of the
                  cafe's reporting. */}
              <Button
                variant="secondary"
                className="flex-1"
                disabled={busy}
                onClick={() => stopSession('cancelled')}
              >
                Cancel — started by mistake
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
